import { toast } from "@/components/common/Toast"
import {
  useUpdateTemplateSettings,
  type TemplateSettingsPatch,
} from "@/features/material-compliance/api/templateSettingsApi"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"

export type TemplateSettingsSaver = ReturnType<typeof useTemplateSettingsSave>

/**
 * Saves part of the org's inquiry settings and confirms it; a failure is
 * already toasted by the mutation itself.
 *
 * One per page, shared by all its Save buttons: each save re-reads the
 * settings and writes them back whole, so two saves in flight at once could
 * undo each other. `busy` blocks every button while one runs; `savingKey`
 * says which one shows the spinner.
 */
export function useTemplateSettingsSave() {
  const { translate } = useTranslation()
  const mutation = useUpdateTemplateSettings()
  const [savingKey, setSavingKey] = useState<string | null>(null)
  // The button whose save went through last, for its "Saved" mark.
  const [savedKey, setSavedKey] = useState<string | null>(null)

  async function save(
    key: string,
    patch: TemplateSettingsPatch,
  ): Promise<boolean> {
    setSavingKey(key)
    setSavedKey(null)
    try {
      await mutation.mutateAsync(patch)
      toast.success(translate("SETTINGS/SAVED"))
      setSavedKey(key)
      return true
    } catch {
      return false
    } finally {
      setSavingKey(null)
    }
  }

  return {
    save,
    savingKey,
    savedKey,
    busy: mutation.isPending,
    /** Saving needs MC_INQUIRIES_SEND; everyone else sees the values only. */
    canEdit: mutation.canRun,
  }
}
